/**
 * Deflection modal — offered to customers who have progressed far enough in
 * booking to suggest they skip straight to online apply (RCP).
 *
 * a11y (rcp-security B.5):
 *   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` on the title.
 *   - Focus trap inside the modal — Tab cycles among modal focusables; Escape
 *     and click-on-scrim close the modal.
 *   - Close button has aria-label so it's reachable by AT.
 */

import { useEffect, useRef } from 'react';
import { Button } from '@/components/primitives';

export interface ApplyOnlineInterventionProps {
  open: boolean;
  onClose: () => void;
  onApplyOnline: () => void;
}

export function ApplyOnlineIntervention({ open, onClose, onApplyOnline }: ApplyOnlineInterventionProps): JSX.Element | null {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    const firstFocusable = modalRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    firstFocusable?.focus();

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="rae-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="rae-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rae-apply-title"
        ref={modalRef}
      >
        <button
          type="button"
          className="rae-modal__close"
          onClick={onClose}
          aria-label="Close apply-online offer"
        >
          ✕
        </button>

        <span className="rae-modal__badge">⚡ Faster option</span>
        <h1 id="rae-apply-title" style={{ fontSize: 26, color: 'var(--pcb-blue-dark)', fontWeight: 700, marginBottom: 'var(--sp-6)' }}>
          No appointment necessary
        </h1>
        <p style={{ fontSize: 15, color: 'var(--ink-700)', marginBottom: 'var(--sp-4)' }}>
          You can open a Phelps County Bank account online right now.
        </p>
        <div style={{ fontSize: 14, color: 'var(--pcb-green)', fontWeight: 500, marginBottom: 'var(--sp-9)' }}>
          <span aria-hidden style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--pcb-green)', borderRadius: '50%', marginRight: 6, verticalAlign: 'middle' }} />
          Takes about <strong>10 minutes</strong> start to finish
        </div>

        <ul className="rae-modal__perks">
          <li>Get your account number before you leave your couch</li>
          <li>Fund it from another bank, debit card, or later at a branch</li>
          <li>Same deposit products, same rates, same employee-owners</li>
        </ul>

        <Button variant="primary" fullWidth onClick={onApplyOnline}>
          APPLY NOW
        </Button>
        <button
          type="button"
          className="btn btn-link"
          style={{ display: 'block', width: '100%', marginTop: 'var(--sp-5)', color: 'var(--ink-500)', textDecoration: 'underline' }}
          onClick={onClose}
        >
          Book an appointment instead
        </button>
      </div>
    </div>
  );
}
